import type { AgentPlatform } from "@/api/temp/AgentEstateService";
import type {
  AgentActivitySummary,
  AgentFleetProtection,
  AgentGovernanceScenario,
  DataGovernanceScenario,
  DataGovernanceSummary,
  DataPlatformSummary,
  DataSourceSummary,
  FleetCell,
  FleetDimension,
  FleetGroup,
} from "@/api/temp/GovernanceOverviewService";

// ─────────────────────────────────────────────────────────────────────────────
// The approved Governance Overview reference fixtures (dashboard handoff,
// `dashboard-reference-fixtures.json`). Illustrative only: served inside
// `<MockScope>`, never a production fallback.
//
// Every total is derived from the rows below, never authored next to them, so
// a tab badge, a legend and a table can only ever agree.
// ─────────────────────────────────────────────────────────────────────────────

type CategoryTuple = [key: string, count: number];

interface PlatformFixture {
  id: string;
  name: string;
  sources: number;
  withAlerts: number;
  categories: CategoryTuple[];
}

/** Attention state: 36 sources, 13 platforms, 18 with alerts, 248 alerts. */
const DATA_PLATFORMS: PlatformFixture[] = [
  {
    id: "snowflake",
    name: "Snowflake",
    sources: 7,
    withAlerts: 7,
    categories: [
      ["regulatory_compliance", 68],
      ["shadow_it", 20],
      ["overly_granted_access", 4],
      ["data_exfiltration", 4],
    ],
  },
  {
    id: "databricks",
    name: "Databricks",
    sources: 5,
    withAlerts: 4,
    categories: [
      ["regulatory_compliance", 38],
      ["shadow_it", 20],
      ["overly_granted_access", 6],
    ],
  },
  {
    id: "sqlserver",
    name: "SQL Server",
    sources: 3,
    withAlerts: 2,
    categories: [
      ["overly_granted_access", 24],
      ["shadow_it", 8],
    ],
  },
  {
    id: "oracle",
    name: "Oracle",
    sources: 2,
    withAlerts: 1,
    categories: [
      ["shadow_it", 8],
      ["regulatory_compliance", 4],
    ],
  },
  {
    id: "mysql",
    name: "MySQL",
    sources: 3,
    withAlerts: 1,
    categories: [
      ["overly_granted_access", 6],
      ["data_exfiltration", 2],
    ],
  },
  { id: "powerbi", name: "Power BI", sources: 7, withAlerts: 0, categories: [] },
  {
    id: "postgresql",
    name: "PostgreSQL",
    sources: 2,
    withAlerts: 1,
    categories: [
      ["overly_granted_access", 12],
      ["regulatory_compliance", 4],
    ],
  },
  {
    // No `AccountTypes` entry exists for Redshift; the id is still the
    // lowercase service key the data-source list would filter on.
    id: "redshift",
    name: "Amazon Redshift",
    sources: 2,
    withAlerts: 1,
    categories: [
      ["regulatory_compliance", 8],
      ["shadow_it", 4],
    ],
  },
  {
    id: "bigquery",
    name: "BigQuery",
    sources: 1,
    withAlerts: 1,
    categories: [
      ["regulatory_compliance", 6],
      ["data_exfiltration", 2],
    ],
  },
  { id: "s3", name: "Amazon S3", sources: 1, withAlerts: 0, categories: [] },
  {
    id: "dynamodb",
    name: "Amazon DynamoDB",
    sources: 1,
    withAlerts: 0,
    categories: [],
  },
  {
    id: "aurora",
    name: "Amazon Aurora",
    sources: 1,
    withAlerts: 0,
    categories: [],
  },
  {
    id: "glue_data_pipeline",
    name: "AWS Glue",
    sources: 1,
    withAlerts: 0,
    categories: [],
  },
];

/** Sources whose setup is incomplete in the attention state. */
const INCOMPLETE_SOURCE_IDS = new Set(["oracle-01", "mysql-01"]);

/** `total` split across `parts`, the remainder going to the first parts. */
const evenSplit = (total: number, parts: number): number[] =>
  Array.from(
    { length: parts },
    (_, index) =>
      Math.floor(total / parts) + (index < total % parts ? 1 : 0),
  );

/**
 * One row per source: the first `withAlerts` sources of a platform carry its
 * alerts, each category split across them, so the sources view and the
 * platforms view count the same population.
 */
const buildSources = (healthy: boolean): DataSourceSummary[] =>
  DATA_PLATFORMS.flatMap(platform => {
    const affected = healthy ? 0 : platform.withAlerts;
    const splits = platform.categories.map(([key, count]) => ({
      key,
      parts: evenSplit(count, Math.max(affected, 1)),
    }));
    return Array.from({ length: platform.sources }, (_, index) => {
      const id = `${platform.id.replace(/_/g, "-")}-${String(index + 1).padStart(2, "0")}`;
      const categories =
        index < affected
          ? splits
              .map(({ key, parts }) => ({ key, alertCount: parts[index] }))
              .filter(category => category.alertCount > 0)
          : [];
      return {
        id,
        name: id,
        platformId: platform.id,
        openAlerts: categories.reduce((sum, row) => sum + row.alertCount, 0),
        setupComplete: healthy || !INCOMPLETE_SOURCE_IDS.has(id),
        categories,
      };
    });
  });

const buildPlatforms = (
  sources: DataSourceSummary[],
): DataPlatformSummary[] =>
  DATA_PLATFORMS.map(platform => {
    const rows = sources.filter(source => source.platformId === platform.id);
    const counts = new Map<string, number>();
    rows.forEach(row =>
      row.categories.forEach(({ key, alertCount }) =>
        counts.set(key, (counts.get(key) ?? 0) + alertCount),
      ),
    );
    return {
      id: platform.id,
      name: platform.name,
      sources: rows.length,
      sourcesWithOpenAlerts: rows.filter(row => row.openAlerts > 0).length,
      openAlerts: rows.reduce((sum, row) => sum + row.openAlerts, 0),
      categories: [...counts.entries()]
        .map(([key, alertCount]) => ({ key, alertCount }))
        .sort((left, right) => right.alertCount - left.alertCount),
    };
  });

/** A recent evaluation, relative to now — never the prototype's fixed date. */
const recentEvaluation = (): string =>
  new Date(Date.now() - 18 * 60 * 1000).toISOString();

export const buildDataGovernanceSummary = (
  scenario: DataGovernanceScenario = "attention",
): DataGovernanceSummary => {
  if (scenario === "empty")
    return {
      evaluatedAt: null,
      totalSources: 0,
      monitoringPolicies: null,
      accessControls: null,
      sourcesWithIncompleteSetup: null,
      platforms: [],
      sources: [],
    };
  const healthy = scenario === "healthy";
  const sources = buildSources(healthy);
  return {
    evaluatedAt: recentEvaluation(),
    totalSources: sources.length,
    monitoringPolicies: { enabled: healthy ? 64 : 45, total: 64 },
    accessControls: healthy
      ? { deployed: 69, total: 69, saved: 0, failed: 0 }
      : { deployed: 67, total: 69, saved: 1, failed: 1 },
    sourcesWithIncompleteSetup: sources.filter(row => !row.setupComplete)
      .length,
    platforms: buildPlatforms(sources),
    sources,
  };
};

// ─────────────────────────────────────────────────────────────────── agents

const FLEET_PLATFORMS: AgentPlatform[] = [
  "MICROSOFT_FOUNDRY",
  "AWS_AGENTCORE",
  "DATABRICKS",
  "SNOWFLAKE",
];

type CellTuple = [unprotected: number, protectedCount: number] | null;

/**
 * Business unit × platform, in `FLEET_PLATFORMS` order; `null` is "Not
 * connected". Ids are the agent estate's canonical business-unit values.
 */
const FLEET_MATRIX: Array<[id: string, cells: CellTuple[]]> = [
  ["ENGINEERING", [[61, 25], [25, 9], [34, 18], [0, 0]]],
  ["ANALYTICS", [[52, 18], [34, 18], [34, 18], [18, 16]]],
  ["FINANCE", [[34, 52], [34, 35], [36, 34], [18, 16]]],
  ["CUSTOMER_SUPPORT", [[34, 34], [25, 27], [34, 36], [27, 7]]],
  ["HR", [[18, 34], null, [18, 16], [25, 45]]],
];

const toCell = (tuple: CellTuple, healthy: boolean): FleetCell =>
  tuple
    ? {
        connection: "connected",
        unprotected: healthy ? 0 : tuple[0],
        protected: healthy ? tuple[0] + tuple[1] : tuple[1],
      }
    : { connection: "not-connected" };

/**
 * Department and geography memberships, by business unit. Each regroups the
 * same agents, so every grouping partitions the same 1,019 - a group's cells
 * are the sums of its business units' cells, never relabelled copies.
 */
const MEMBERSHIP: Record<
  Exclude<FleetDimension, "BUSINESS_UNIT">,
  Array<[id: string, businessUnits: string[]]>
> = {
  DEPARTMENT: [
    ["QUANT_ANALYTICS", ["ANALYTICS"]],
    ["TRADING_AND_SALES", ["FINANCE", "ENGINEERING"]],
    ["CLIENT_AND_PEOPLE", ["CUSTOMER_SUPPORT", "HR"]],
  ],
  GEOGRAPHY: [
    ["AMERICAS", ["FINANCE", "CUSTOMER_SUPPORT"]],
    ["EMEA", ["ANALYTICS", "HR"]],
    ["APAC", ["ENGINEERING"]],
  ],
};

/**
 * A group's cell: connected when any member is, with the members' counts
 * summed; not connected only when no member is.
 */
const mergeCells = (cells: FleetCell[]): FleetCell => {
  const connected = cells.filter(
    (cell): cell is Extract<FleetCell, { connection: "connected" }> =>
      cell.connection === "connected",
  );
  return connected.length
    ? {
        connection: "connected",
        unprotected: connected.reduce((sum, cell) => sum + cell.unprotected, 0),
        protected: connected.reduce((sum, cell) => sum + cell.protected, 0),
      }
    : { connection: "not-connected" };
};

const MEMBERSHIP_DIMENSIONS = Object.keys(MEMBERSHIP) as Array<
  keyof typeof MEMBERSHIP
>;

const businessUnitGroups = (healthy: boolean): FleetGroup[] =>
  FLEET_MATRIX.map(([id, cells]) => ({
    id,
    businessUnits: [id],
    cells: Object.fromEntries(
      cells.map((cell, index) => [
        FLEET_PLATFORMS[index],
        toCell(cell, healthy),
      ]),
    ),
  }));

const groupsFor = (
  dimension: FleetDimension,
  healthy: boolean,
): FleetGroup[] => {
  const units = businessUnitGroups(healthy);
  if (dimension === "BUSINESS_UNIT") return units;
  const byId = new Map(units.map(unit => [unit.id, unit]));
  return MEMBERSHIP[dimension].map(([id, members]) => ({
    id,
    businessUnits: members,
    cells: Object.fromEntries(
      FLEET_PLATFORMS.map(platform => [
        platform,
        mergeCells(members.map(member => byId.get(member)!.cells[platform]!)),
      ]),
    ),
  }));
};

export const buildAgentFleetProtection = (
  scenario: AgentGovernanceScenario = "attention",
  dimension: FleetDimension = "BUSINESS_UNIT",
): AgentFleetProtection => {
  const groups =
    scenario === "empty" ? [] : groupsFor(dimension, scenario === "healthy");
  // Every grouping partitions the agents, so summing the cells is the
  // distinct total.
  let unprotected = 0;
  let protectedCount = 0;
  groups.forEach(group =>
    Object.values(group.cells).forEach(cell => {
      if (cell?.connection !== "connected") return;
      unprotected += cell.unprotected;
      protectedCount += cell.protected;
    }),
  );
  return {
    evaluatedAt: scenario === "empty" ? null : recentEvaluation(),
    dimension,
    availableDimensions: ["BUSINESS_UNIT", ...MEMBERSHIP_DIMENSIONS],
    platforms: scenario === "empty" ? [] : FLEET_PLATFORMS,
    totalAgents: unprotected + protectedCount,
    unprotected,
    protected: protectedCount,
    groups,
  };
};

const ATTENTION_ACTIVITY: AgentActivitySummary = {
  windowHours: 24,
  authorizationDecisions: 1_240_000,
  policiesApplied: 89,
  agentsWithIntentDrift: 15,
  newAgentsImported: 12,
};

/** A confirmed quiet day; also what an estate with no agents records. */
const NO_ACTIVITY: AgentActivitySummary = {
  windowHours: 24,
  authorizationDecisions: 0,
  policiesApplied: 0,
  agentsWithIntentDrift: 0,
  newAgentsImported: 0,
};

const ACTIVITY: Record<AgentGovernanceScenario, AgentActivitySummary> = {
  attention: ATTENTION_ACTIVITY,
  healthy: { ...ATTENTION_ACTIVITY, agentsWithIntentDrift: 0 },
  quiet: NO_ACTIVITY,
  empty: NO_ACTIVITY,
};

export const buildAgentActivitySummary = (
  scenario: AgentGovernanceScenario = "attention",
): AgentActivitySummary => ACTIVITY[scenario];
